/*
 * Copyright (c) 2021, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React from 'react';
import {useParams} from "react-router-dom";
import { makeStyles, Theme } from '@material-ui/core/styles';

import { Container, CssBaseline, Typography } from '@material-ui/core';
import { RESOURCE_LOGIN } from "../../data/constants"
import { AsgardeoSPAClient } from '@asgardeo/auth-react';
import { FormattedMessage, useIntl } from 'react-intl';

const useStyles = makeStyles((theme: Theme) => ({
    signInContainer: {
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
    },
    signInText: {
        fontFamily: 'GilmerBold',
        fontSize: '29px',
        marginTop: '50px'
    },
    loaderContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
    },
    link: {
        color: theme.palette.primary.main,
        cursor: "pointer",
        textDecoration: "underline",
        "&:hover, &:focus, &:active": {
            color: theme.palette.primary.main,
            textDecoration: "underline",
        }
    }
}));

export default function Unauthorized() {
    const intl = useIntl();
    const classes = useStyles();
    const {error_code: errorCode} = useParams<{error_code: string}>();
    const authClient = AsgardeoSPAClient.getInstance();
    let errorLine1;
    let errorLine2;

    const login = <a className={classes.link} href={window.location.origin + RESOURCE_LOGIN}>Go back to login</a>;

    const handleSignOut = () => {
        // Sigining out the user from current account to sgin in with another account
        authClient?.signOut();
    }

    switch (errorCode) {
        case "500":
            errorLine1 = intl.formatMessage({
                    id: 'components.shared.unauthorized.line1.internalServerError.message.content',
                    defaultMessage: "You are not authorized to access this site.",
                });
            errorLine2 = <div><FormattedMessage
                    id="components.shared.unauthorized.line2.internalServerError.message.start.content"
                    defaultMessage="Try to "
                /><span className={classes.link} onClick={handleSignOut}><FormattedMessage
                    id="components.shared.unauthorized.line2.internalServerError.message.middle.content"
                    defaultMessage="signin"
                /></span><FormattedMessage
                    id="components.shared.unauthorized.line2.internalServerError.message.end.content"
                    defaultMessage=" with a registered email address."
                /></div>;
            break;
        case "1020":
        case "1021":
            errorLine1 = intl.formatMessage({
                    id: 'components.shared.unauthorized.line1.pending.message.content',
                    defaultMessage: "You are currently not authorized to access this site. Your request to signup for the specific organization on Choreo is awaiting approval.",
                });
            errorLine2 = <div><FormattedMessage
                    id="components.shared.unauthorized.line2.pending.message.start.content"
                    defaultMessage="Go back to the "
                /><span className={classes.link} onClick={handleSignOut}><FormattedMessage
                    id="components.shared.unauthorized.line2.pending.message.middle.content"
                    defaultMessage="Home page"
                /></span><FormattedMessage
                    id="components.shared.unauthorized.line2.pending.message.end.content"
                    defaultMessage=" of the Developer Portal."
                /></div>;
            break;
        case "1022":
            errorLine1 = intl.formatMessage({
                    id: 'components.shared.unauthorized.line1.rejected.message.content',
                    defaultMessage: "You are not authorized to access this site. The admin has declined your request to signup for the specific organization.",
                });
            errorLine2 = <div><FormattedMessage
                    id="components.shared.unauthorized.line2.rejected.message.start.content"
                    defaultMessage="Go back to the "
                /><span className={classes.link} onClick={handleSignOut}><FormattedMessage
                    id="components.shared.unauthorized.line2.rejected.message.middle.content"
                    defaultMessage="Home page"
                /></span><FormattedMessage
                    id="components.shared.unauthorized.line2.rejected.message.end.content"
                    defaultMessage=" of the Developer Portal, select your organization, and sign in with a different email address."
                /></div>;
            break;
        default:
            errorLine1 = intl.formatMessage({
                    id: 'components.shared.unauthorized.line1.default.message.content',
                    defaultMessage: "Unknown authorization error occurred.",
                });
            errorLine2 = <div>{login}</div>;
    }

    return (
        <div>
            <CssBaseline />
            <Container maxWidth="lg" className={classes.signInContainer}>
                <div className={classes.loaderContainer}>
                    <img src="../../themes/default/images/page-not-found.svg"/>
                </div>
                <Typography className={classes.signInText}>{errorLine1}</Typography>
                <Typography>
                    {errorLine2}
                </Typography>
            </Container>
        </div>
    );
}
